/**
 * Stride Thera Injector
 * Date: 04-04-2025
 * Author: Fnu Kajol
 * Description: This file contains the functions to interact with the Stride Thera EMR
 * Notes:
 *
 *
 */
import type { FocusCaptureSource, SectionRead } from './NoteFocusCapture.ts';
export { getSchedule } from './StrideSchedule.ts';

const DEFAULT_DOB = '05/29/2023';

// Helper function to sanitize keys for questions
function sanitizeKey(key) {
  return key.replace(/[:?"']/g, '').trim();
}

// COM-438: Stride's back end stores injected text verbatim, so control and
// non-Latin characters surface as NULLs or "?" downstream. Strip them here.
const EMR_CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g;
const EMR_INVISIBLE_CHARS = /[\u200B-\u200F\u2060\uFEFF\uFFFD]/g;
const EMR_BOX_DRAWING_CHARS = /[\u2500-\u259F]/g;
const EMR_CHAR_FOLDS: Array<[RegExp, string]> = [
  [/[\u2018\u2019\u201A\u201B]/g, "'"],
  [/[\u201C\u201D\u201E\u201F]/g, '"'],
  [/[\u2010-\u2015]/g, '-'],
  [/\u2026/g, '...'],
  [/\u00A0/g, ' '],
  [/[\u2028\u2029]/g, '\n']
];

export function sanitizeEmrText(value: unknown): string {
  if (value === null || value === undefined) return '';
  let text = String(value).normalize('NFC');
  for (const [pattern, replacement] of EMR_CHAR_FOLDS) {
    text = text.replace(pattern, replacement);
  }
  return text
    .replace(EMR_CONTROL_CHARS, '')
    .replace(EMR_INVISIBLE_CHARS, '')
    .replace(EMR_BOX_DRAWING_CHARS, '');
}

/**
 * In Objective structure, if a key's structure contains only Comments and/or Other,
 * rename the key to "Key - comments" (e.g. "Range of Motion" -> "Range of Motion - comments").
 */
function normalizeObjectiveStructureKeys() {
  const obj = questionInteractions['Objective'];
  if (!obj?.Structure) return;

  const structure = obj.Structure;
  const keysToRename: Array<{ old: string; new: string }> = [];

  for (const [fieldKey, fieldVal] of Object.entries(structure)) {
    if (!fieldVal || typeof fieldVal !== 'object') continue;

    const subKeys = Object.keys(fieldVal);
    const allCommentsOrOther = subKeys.every((k) => {
      const lower = k.toLowerCase().trim();
      return lower === 'comments' || lower === 'other';
    });

    if (subKeys.length > 0 && allCommentsOrOther) {
      keysToRename.push({ old: fieldKey, new: `${fieldKey} - comments` });
    }
  }

  const subEls = obj.SubsectionElements;
  for (const { old: oldKey, new: newKey } of keysToRename) {
    structure[newKey] = structure[oldKey];
    delete structure[oldKey];
    if (subEls && subEls[oldKey]) {
      subEls[newKey] = subEls[oldKey];
      delete subEls[oldKey];
    }
  }
}

/** Main Functions */
export function getPageInfo() {
  /*
        Description: This function retrieves the page information, including the base URL and note type, 
                     by parsing the HTML structure.
        Return: A dictionary with the page information
            {
                "URL": Base URL for the page,
                "Note Type": "PT Daily/SOAP Note",...
            }
        */

  const hostname = window.location.hostname;
  const baseURL = hostname.split('.').slice(-2).join('.');
  const noteType = getNoteType(); // Call the function to get the note type dynamically

  return {
    URL: baseURL,
    'Note Type': noteType || 'Default Note'
  };
}

/**
 * The full note title as written in the DOM, e.g. "Hands On Therapy - Initial
 * Evaluation". getNoteType() trims this down to the part before the dash; use
 * this when the discipline/template suffix matters.
 */
function getRawNoteType(): string | null {
  const boxes = document.querySelectorAll('[class^="MuiBox-root"]');
  if (!boxes || boxes.length === 0) return null;

  let noteType = null;

  boxes.forEach((box) => {
    const svg =
      box.querySelector('svg[aria-label]') || box.querySelector('svg[title]');
    if (svg) {
      const titleAttr =
        svg.getAttribute('aria-label') || svg.getAttribute('title');
      if (typeof titleAttr === 'string' && titleAttr.trim()) {
        noteType = titleAttr.trim();
      }
    }
  });

  return noteType || null;
}

function getNoteType() {
  const noteType = getRawNoteType();
  if (!noteType) return null;

  // Return only the part before the dash, trimmed
  // Daily - Ortho we only want Daily
  const parts = noteType.split('-');
  return parts[0]?.trim() || null;
}

/**
 * The (raw) note type is read from the DOM, so cache it for the duration of a
 * getQuestions() run instead of re-querying the whole document per field.
 */
let cachedNoteType: string | null = null;

function getCachedNoteType(): string {
  if (cachedNoteType === null) {
    cachedNoteType = getRawNoteType() || '';
  }
  return cachedNoteType;
}

function isInitialEvalNote(): boolean {
  return /initial\s*eval/i.test(getCachedNoteType());
}

/**
 * Goals are authored on the Initial Evaluation. On every other note type the
 * goals already exist and the therapist only reports progress against them, so
 * expose Comments and Completion % only - Goal text, Type and Timeframe are
 * dropped so they never get rewritten on a follow-up note.
 *
 * The surviving fields are re-keyed per goal ("Goal 1 Comments", "Goal 1
 * Completion %", ...) and carry the goal's own text in their Description, so
 * it is unambiguous which goal each answer belongs to.
 */
function pruneGoalsForNonInitialEval() {
  if (isInitialEvalNote()) return;

  // "Comments" -> { base: 'Comments', index: 1 }, "Comments (2)" -> index 2
  const parseFieldKey = (key: string) => {
    const match = key.match(/^(.*?)\s*\((\d+)\)\s*$/);
    return match
      ? { base: match[1].trim(), index: parseInt(match[2], 10) }
      : { base: key.trim(), index: 1 };
  };

  const collapseWhitespace = (text: string) => text.replace(/\s+/g, ' ').trim();

  for (const section of Object.values(questionInteractions) as any[]) {
    const structure = section?.Structure;
    if (!structure) continue;

    for (const [subKey, subVal] of Object.entries(structure)) {
      if (!subKey.toLowerCase().includes('goal')) continue;
      if (!subVal || typeof subVal !== 'object') continue;

      const fields = subVal as Record<string, any>;
      const goalTexts: Record<number, string> = {};
      const commentFields: Record<number, any> = {};
      const completionFields: Record<number, any> = {};

      for (const [fieldKey, fieldVal] of Object.entries(fields)) {
        const { base, index } = parseFieldKey(fieldKey);
        const baseLower = base.toLowerCase();

        if (baseLower === 'goal') {
          const text = collapseWhitespace(
            String(fieldVal?.['Existing Value'] ?? '')
          );
          if (text) goalTexts[index] = text;
        } else if (baseLower.startsWith('comment')) {
          commentFields[index] = fieldVal;
        } else if (baseLower.includes('completion')) {
          completionFields[index] = fieldVal;
        }
      }

      const goalIndexes = Array.from(
        new Set([
          ...Object.keys(commentFields).map(Number),
          ...Object.keys(completionFields).map(Number)
        ])
      ).sort((a, b) => a - b);

      // Rebuild the subsection so the fields come back in goal order
      for (const fieldKey of Object.keys(fields)) delete fields[fieldKey];

      goalIndexes.forEach((goalIndex) => {
        const goalText = goalTexts[goalIndex];
        const goalRef = goalText
          ? `goal ${goalIndex} ("${goalText}")`
          : `goal ${goalIndex}`;

        if (commentFields[goalIndex]) {
          fields[`Goal ${goalIndex} Comments`] = {
            ...commentFields[goalIndex],
            Description: `Progress comment for ${goalRef}. Only comment on this goal.`
          };
        }

        if (completionFields[goalIndex]) {
          fields[`Goal ${goalIndex} Completion %`] = {
            ...completionFields[goalIndex],
            Type: 'Integer',
            Description: `Percent complete (0-100) for ${goalRef}. Value should be a integer`
          };
        }
      });

      // Nothing left to fill in - drop the subsection entirely
      if (Object.keys(fields).length === 0) {
        delete structure[subKey];
        if (section.SubsectionElements) {
          delete section.SubsectionElements[subKey];
        }
      }
    }
  }
}

export function getPatientInfo() {
  /*
          Description: This function retrieves patient information from the document,
          including the EMR ID, first name, last name, and date of birth (DOB).
          Returns: An object with patient information
          {
              "EMR ID": emrID,
              "First Name": firstName,
              "Last Name": lastName,
              "DOB": dob,
          }
        */

  let emrID = null;
  let firstName = null;
  let lastName = null;
  let dob = DEFAULT_DOB;

  // Select the anchor element that contains the patient's name
  const nameElement = document.querySelector('a[href^="/patients/"]'); // Selects any anchor with href starting with "/patients/"

  if (nameElement) {
    const fullName = nameElement.textContent.trim();
    emrID = fullName;
    const nameParts = fullName.split(' '); // Split the name into parts
    if (nameParts.length > 1) {
      firstName = nameParts[0];
      lastName = nameParts[nameParts.length - 1];
    }
  } else {
    console.log('Name element not found');
  }

  const dobLabel = Array.from(
    document.querySelectorAll('[class^="MuiBox-root-"]')
  ).find((el) => el.textContent.trim() === 'Date Of Birth');

  dob =
    dobLabel?.nextElementSibling?.textContent?.trim().split(' ')[0] ||
    DEFAULT_DOB;

  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: dob
  };
}

/* 
questions - This is where we will store the questions
          - a list of dictionaries
     [{
        "Question": "Where was the injury?", 
        "Type": "Multiple Choice", 
        "Choices": ["Back", "Neck", "Shoulder"]
     },
        ...
        ]

*/
var questions = [];

/*
question Interactions - This is where we will store the interactions for each question
                        - a dictionary of dictionaries
         {
            "Where was the injury?": {
                "Type": "Multiple Choice",
                "Choices"{
                    "Back": () => {click the back button},
                    "Neck": () => {click the neck button},
                    "Shoulder": () => {click the shoulder button}
                }
            },
        },
        {
            "What is the injury?": {
                "Type": "Long Answer",
                "Input": (val) => {input the value into the input box}
        }
            ...
         }
*/

var questionInteractions = {};

/**
 * True only while readSectionForFocusCapture runs the extractors. It
 * makes them record the current value of every field, including the empty and
 * click-driven ones that getQuestions deliberately leaves without an
 * Existing Value, so getQuestions' own output never changes.
 */
let capturingValues = false;

/**
 * Set only while readSectionForFocusCapture runs: the one section of the note
 * the extractors read. Everywhere else they read the whole document.
 */
let captureScope: Element | null = null;

function queryNote(selector: string) {
  return (captureScope ?? document).querySelectorAll(selector);
}

export function getQuestions() {
  console.log('[StrideTheraInject.ts] getQuestions called');
  questions = [];
  questionInteractions = {};
  cachedNoteType = null; // re-read the note type for this run
  extractTextInputsAndTextareas();
  extractCheckboxes();
  extractSingleChoiceToggles();
  extractObjectiveTable();
  extractFlowsheet();
  // getCheckboxQuestionsStructured();

  // In Objective, rename structure keys that contain only Comments/Other to "Key - comments"
  normalizeObjectiveStructureKeys();

  // Outside of Initial Evals, Goals keeps only Comments and Completion %
  pruneGoalsForNonInitialEval();

  Object.keys(questionInteractions)
    .sort((a, b) => {
      const elementA = questionInteractions[a].Elements[0];
      const elementB = questionInteractions[b].Elements[0];

      if (elementA && elementB) {
        if (
          elementA.compareDocumentPosition(elementB) &
          Node.DOCUMENT_POSITION_FOLLOWING
        ) {
          return -1;
        } else {
          return 1;
        }
      } else if (elementA) return -1;
      else if (elementB) return 1;
      else return 0;
    })
    .forEach((key) => {
      const q = questionInteractions[key];
      const isFlexWithEmptyStructure =
        q.Type === 'Flex' && Object.keys(q.Structure || {}).length === 0;
      if (!isFlexWithEmptyStructure) {
        const sanitizedKey = sanitizeKey(key);
        const q = questionInteractions[key];
        const question: Record<string, unknown> = {
          ...q,
          Question: sanitizedKey,
          Key: sanitizedKey
        };
        delete question.Elements;

        // Sort Structure keys by DOM order for View More subsections
        if (
          q.Type === 'Flex' &&
          q.Structure &&
          q.SubsectionElements &&
          Object.keys(q.Structure).length > 0
        ) {
          const sortedKeys = Object.keys(q.Structure).sort((a, b) => {
            const elA = q.SubsectionElements[a];
            const elB = q.SubsectionElements[b];
            if (!elA && !elB) return 0;
            if (!elA) return 1;
            if (!elB) return -1;
            return elA.compareDocumentPosition(elB) &
              Node.DOCUMENT_POSITION_FOLLOWING
              ? -1
              : 1;
          });
          const orderedStructure: Record<string, unknown> = {};
          sortedKeys.forEach((k) => {
            orderedStructure[k] = q.Structure[k];
          });
          question.Structure = orderedStructure;
        }
        delete question.SubsectionElements;
        questions.push(question);
      }
    });

  return questions;
}

function extractTextInputsAndTextareas() {
  // Find sections by looking for section headers in the new MUI structure
  const knownSectionNames = [
    'Subjective',
    'Objective',
    'Assessment',
    'Plan',
    'Flowsheet'
  ];
  const sectionMap = new Map();

  // Find section headers by looking for divs with section names
  knownSectionNames.forEach((sectionName) => {
    // Look for headers that contain the section name
    const headers = Array.from(queryNote('div')).filter(
      (div) => {
        const text = div.textContent?.trim();
        return (
          text === sectionName &&
          (div.className.includes('header') ||
            div.closest('[class*="elementContainer"]')
              ?.previousElementSibling === div ||
            div.parentElement?.querySelector('[class*="elementContainer"]'))
        );
      }
    );

    if (headers.length > 0) {
      const header = headers[0];
      // Find the parent container that holds this section's content
      let sectionContainer = header.parentElement;
      // Look for the main section container
      while (sectionContainer && sectionContainer !== document.body) {
        // Check if this container has element containers or form fields
        const hasElementContainers = sectionContainer.querySelector(
          '[class*="elementContainer"]'
        );
        const hasFields = sectionContainer.querySelector(
          'textarea:not([aria-hidden="true"]), input[type="text"]:not([readonly])'
        );
        if (hasElementContainers || hasFields) {
          break;
        }
        sectionContainer = sectionContainer.parentElement;
      }

      if (sectionContainer && sectionContainer !== document.body) {
        sectionMap.set(sectionName, sectionContainer);
      }
    }
  });

  // Also try to find sections by looking for element containers and working backwards
  const elementContainers = queryNote('[class*="elementContainer"]');
  if (elementContainers.length > 0 && sectionMap.size === 0) {
    // Group element containers by finding their section headers
    const containersBySection = new Map();

    elementContainers.forEach((container) => {
      // Look backwards for a section header
      let current = container.previousElementSibling;
      let sectionName = 'Unknown';
      let depth = 0;

      while (current && depth < 5) {
        const text = current.textContent?.trim();
        if (text && knownSectionNames.includes(text)) {
          sectionName = text;
          break;
        }
        // Also check parent siblings
        if (current.parentElement) {
          const siblings = Array.from(current.parentElement.children);
          const headerIndex = siblings.indexOf(current);
          for (let i = headerIndex - 1; i >= 0 && i >= headerIndex - 3; i--) {
            const siblingText = siblings[i].textContent?.trim();
            if (siblingText && knownSectionNames.includes(siblingText)) {
              sectionName = siblingText;
              break;
            }
          }
        }
        if (sectionName !== 'Unknown') break;
        current = current.previousElementSibling;
        depth++;
      }

      // Find the section container
      let sectionContainer = container.closest('div');
      while (sectionContainer && sectionContainer !== document.body) {
        const hasMultipleContainers =
          sectionContainer.querySelectorAll('[class*="elementContainer"]')
            .length > 1;
        if (hasMultipleContainers) {
          break;
        }
        sectionContainer = sectionContainer.parentElement;
      }

      if (!containersBySection.has(sectionName)) {
        containersBySection.set(
          sectionName,
          sectionContainer || container.parentElement
        );
      }
    });

    containersBySection.forEach((container, sectionName) => {
      if (container) {
        sectionMap.set(sectionName, container);
      }
    });
  }

  // Process each section found
  sectionMap.forEach((sectionContainer, sectionName) => {
    const sanitizedSectionName = sanitizeKey(sectionName);

    if (!questionInteractions[sanitizedSectionName]) {
      questionInteractions[sanitizedSectionName] = {
        Type: 'Flex',
        Key: sanitizedSectionName,
        Question: sanitizedSectionName,
        Structure: {},
        SubsectionElements: {},
        Elements: []
      };
    }

    // Find all element containers within this section
    const elementContainers = sectionContainer.querySelectorAll(
      'div[class*="elementContainer"]'
    );

    if (elementContainers.length === 0) {
      // Fallback: treat the whole section as one subsection
      if (!questionInteractions[sanitizedSectionName].Structure['Untitled']) {
        questionInteractions[sanitizedSectionName].Structure['Untitled'] = {};
        questionInteractions[sanitizedSectionName].SubsectionElements[
          'Untitled'
        ] = sectionContainer;
      }
      processSectionFields(
        sectionContainer,
        sanitizedSectionName,
        'Untitled',
        sectionContainer
      );
    } else {
      // Process each element container as a subsection
      elementContainers.forEach((elementContainer) => {
        // Verify which section this element container actually belongs to
        const verifiedSectionName =
          findSectionNameForContainer(elementContainer);
        const sanitizedVerifiedSectionName = sanitizeKey(verifiedSectionName);

        // Use verified section name if it's valid and different from the assigned section
        let actualSectionName = sanitizedSectionName;
        if (
          sanitizedVerifiedSectionName !== 'Unknown' &&
          sanitizedVerifiedSectionName !== sanitizedSectionName
        ) {
          actualSectionName = sanitizedVerifiedSectionName;

          // Ensure the correct section exists in questionInteractions
          if (!questionInteractions[actualSectionName]) {
            questionInteractions[actualSectionName] = {
              Type: 'Flex',
              Key: actualSectionName,
              Question: actualSectionName,
              Structure: {},
              SubsectionElements: {},
              Elements: []
            };
          }
        }

        // Additional check: if subsection title contains Objective-related keywords, assign to Objective
        const subsectionTitleEl = elementContainer.querySelector(
          '[class*="templateSubsectionTitle"] .MuiBox-root'
        );
        const subSectionLabel =
          subsectionTitleEl?.textContent?.trim() || 'Untitled';

        // Detect Objective section by checking for tables (Objective sections typically contain measurement tables)
        const hasTable =
          elementContainer.querySelector('table.MuiTable-root') !== null;

        if (hasTable && actualSectionName !== 'Objective') {
          actualSectionName = 'Objective';
          if (!questionInteractions[actualSectionName]) {
            questionInteractions[actualSectionName] = {
              Type: 'Flex',
              Key: actualSectionName,
              Question: actualSectionName,
              Structure: {},
              SubsectionElements: {},
              Elements: []
            };
          }
        }

        const sanitizedSubSectionLabel = sanitizeKey(subSectionLabel);

        if (
          !questionInteractions[actualSectionName].Structure[
            sanitizedSubSectionLabel
          ]
        ) {
          questionInteractions[actualSectionName].Structure[
            sanitizedSubSectionLabel
          ] = {};
          questionInteractions[actualSectionName].SubsectionElements[
            sanitizedSubSectionLabel
          ] = elementContainer;
        }

        processSectionFields(
          elementContainer,
          actualSectionName,
          sanitizedSubSectionLabel,
          sectionContainer
        );
      });
    }

    questionInteractions[sanitizedSectionName].Elements.push(sectionContainer);
  });
}

// Helper function to process textareas and inputs within a section/subsection
function processSectionFields(
  container,
  sanitizedSectionName,
  sanitizedSubSectionLabel,
  sectionElement
) {
  const labelCounts = {}; // Count duplicates

  // Helper function to determine if a field should be skipped
  const shouldSkipField = (label) => {
    const normalizedLabel = label.toLowerCase().trim();

    // Always skip these fields
    if (
      normalizedLabel.includes('body parts') ||
      normalizedLabel.includes('body region') ||
      normalizedLabel.includes('treatment diagnosis')
    ) {
      return true;
    }

    // In Strength subsection, only keep Comments - skip all other fields as they are redundant with the table data
    if (sanitizedSubSectionLabel.toLowerCase() === 'strength') {
      return normalizedLabel !== 'comments';
    }

    return false;
  };

  const textareas = container.querySelectorAll(
    'textarea:not([aria-hidden="true"]):not([aria-autocomplete="list"])'
  );
  textareas.forEach((textarea, i) => {
    const baseLabel = generateSmartLabel(
      sectionElement,
      sanitizedSubSectionLabel,
      textarea
    );
    if (!baseLabel) return;
    if (shouldSkipField(baseLabel)) return;
    labelCounts[baseLabel] = (labelCounts[baseLabel] || 0) + 1;
    const label =
      labelCounts[baseLabel] > 1
        ? `${baseLabel} (${labelCounts[baseLabel]})`
        : baseLabel;
    const sanitizedLabel = sanitizeKey(label);

    questionInteractions[sanitizedSectionName].Structure[
      sanitizedSubSectionLabel
    ][sanitizedLabel] = {
      'Existing Value': textarea.value,
      Input: (val, replyType = 'overwrite') => {
        if (isEmptyOrNull(val)) return;
        handleTextareaInput(textarea, val, replyType);
      }
    };
  });

  // Handle text and number inputs
  const textInputs = container.querySelectorAll(
    'input[type="text"]:not([readonly]):not([aria-hidden="true"]):not([aria-autocomplete="list"]):not([placeholder="mm/dd/yyyy"]), input[type="number"]:not([readonly]):not([aria-hidden="true"])'
  );
  textInputs.forEach((input) => {
    if (sanitizedSectionName === 'Objective' && input.closest('table')) return; // skip input under table in Objective

    const baseLabel = generateSmartLabel(
      sectionElement,
      sanitizedSubSectionLabel,
      input
    );
    if (!baseLabel) return;
    if (shouldSkipField(baseLabel)) return;
    labelCounts[baseLabel] = (labelCounts[baseLabel] || 0) + 1;
    const label =
      labelCounts[baseLabel] > 1
        ? `${baseLabel} (${labelCounts[baseLabel]})`
        : baseLabel;
    const sanitizedLabel = sanitizeKey(label);

    const isNumber = input.type === 'number';
    const existingTextVal = (input as HTMLInputElement).value?.trim() || '';

    const fieldConfig: any = {
      ...(existingTextVal || capturingValues
        ? { 'Existing Value': existingTextVal }
        : {}),
      Input: (val, replyType = 'overwrite') => {
        if (isEmptyOrNull(val)) return;
        if (isNumber) {
          const numVal = parseFloat(val.toString());
          if (!isNaN(numVal)) {
            const next = numVal.toString();
            const numberInput = input as HTMLInputElement;

            if (
              numberInput.value !== next &&
              !(numberInput as any).__updating
            ) {
              // Use queue system for all sections to prevent React error #185
              updateQueue.push(() => {
                if ((numberInput as any).__updating) return;
                (numberInput as any).__updating = true;
                setNativeValue(numberInput, next);
                setTimeout(() => {
                  try {
                    numberInput.dispatchEvent(
                      new Event('input', { bubbles: true, cancelable: true })
                    );
                  } finally {
                    delete (numberInput as any).__updating;
                  }
                }, 100);
              });
              processUpdateQueue();
            }

            // Also update slider if present
            const slider = numberInput
              .closest('.MuiFormControl-root')
              ?.querySelector('input[type="range"]') as HTMLInputElement | null;

            if (
              slider &&
              slider.value !== next &&
              !(slider as any).__updating
            ) {
              // Use queue system for all sections
              updateQueue.push(() => {
                if ((slider as any).__updating) return;
                (slider as any).__updating = true;
                setNativeValue(slider, next);
                setTimeout(() => {
                  try {
                    slider.dispatchEvent(
                      new Event('input', { bubbles: true, cancelable: true })
                    );
                  } finally {
                    delete (slider as any).__updating;
                  }
                }, 100);
              });
              processUpdateQueue();
            }
          }
        } else {
          // Check if this is a Timeframe field and clean the value
          if (label.toLowerCase().includes('timeframe')) {
            // Remove "weeks", "week", "days", "day", etc. and keep only numbers or ranges
            const cleanedVal = val
              .toString()
              .replace(/\s*(weeks?|days?|months?|years?)\s*/gi, '')
              .trim();
            handleTextInput(input, cleanedVal, replyType);
          } else {
            handleTextInput(input, val, replyType);
          }
        }
      }
    };

    if (isNumber) {
      fieldConfig.Type = 'Integer';
      fieldConfig.Description = 'Value should be a integer';
    }

    // Add description for Timeframe fields
    const labelLower = label.toLowerCase();
    const isTimeframeField =
      labelLower.includes('timeframe') || labelLower.includes('visit');
    if (isTimeframeField) {
      fieldConfig.Description =
        'Timeframe must be either a single number (ex. "8") or a range (ex. "8-10"). Do not include "weeks", "days", etc.';
    }

    questionInteractions[sanitizedSectionName].Structure[
      sanitizedSubSectionLabel
    ][sanitizedLabel] = fieldConfig;
  });

  // Handle dropdown/autocomplete inputs
  const dropdownInputs = container.querySelectorAll(
    'input[type="text"][aria-autocomplete="list"]:not([readonly]):not([aria-hidden="true"])'
  );
  dropdownInputs.forEach((input) => {
    if (sanitizedSectionName === 'Objective' && input.closest('table')) return; // skip input under table in Objective

    const baseLabel = generateSmartLabel(
      sectionElement,
      sanitizedSubSectionLabel,
      input
    );
    if (!baseLabel) return;
    if (shouldSkipField(baseLabel)) return;
    labelCounts[baseLabel] = (labelCounts[baseLabel] || 0) + 1;
    const label =
      labelCounts[baseLabel] > 1
        ? `${baseLabel} (${labelCounts[baseLabel]})`
        : baseLabel;
    const sanitizedLabel = sanitizeKey(label);

    // Try to extract choices from the autocomplete
    const autocompleteRoot = input.closest('.MuiAutocomplete-root');
    let choices: string[] = [];
    if (autocompleteRoot) {
      // Look for option elements in the autocomplete
      const options = autocompleteRoot.querySelectorAll('[role="option"]');
      if (options.length > 0) {
        choices = Array.from(options)
          .map((opt) => (opt as HTMLElement).textContent?.trim() || '')
          .filter(Boolean);
      }
    }

    const existingDropdownVal = (input as HTMLInputElement).value?.trim() || '';
    const isGoalsSubsectionDrop = sanitizedSubSectionLabel
      .toLowerCase()
      .includes('goal');
    const baseLabelLowerDrop = baseLabel.toLowerCase();
    const isGoalTypeField =
      isGoalsSubsectionDrop && baseLabelLowerDrop === 'type';

    const effectiveChoices =
      isGoalTypeField && choices.length === 0
        ? ['Short Term', 'Long Term']
        : choices;

    const fieldEntry: Record<string, unknown> = {
      ...((existingDropdownVal && !isGoalTypeField) || capturingValues
        ? { 'Existing Value': existingDropdownVal }
        : {}),
      ...(effectiveChoices.length > 0 ? { Choices: effectiveChoices } : {}),
      Input: (val) => {
        if (isEmptyOrNull(val)) return;
        handleDropdownInput(input as HTMLInputElement, val, effectiveChoices);
      }
    };

    questionInteractions[sanitizedSectionName].Structure[
      sanitizedSubSectionLabel
    ][sanitizedLabel] = fieldEntry;
  });

  // Handle date picker inputs
  const datePickerInputs = container.querySelectorAll(
    'input[placeholder="mm/dd/yyyy"]:not([readonly]):not([aria-hidden="true"])'
  );
  datePickerInputs.forEach((input: Element) => {
    const baseLabel = generateSmartLabel(
      sectionElement,
      sanitizedSubSectionLabel,
      input
    );
    if (!baseLabel) return;
    if (shouldSkipField(baseLabel)) return;
    labelCounts[baseLabel] = (labelCounts[baseLabel] || 0) + 1;
    const label =
      labelCounts[baseLabel] > 1
        ? `${baseLabel} (${labelCounts[baseLabel]})`
        : baseLabel;
    const sanitizedLabel = sanitizeKey(label);

    questionInteractions[sanitizedSectionName].Structure[
      sanitizedSubSectionLabel
    ][sanitizedLabel] = {
      ...(capturingValues
        ? { 'Existing Value': (input as HTMLInputElement).value }
        : {}),
      Description: 'Value must be a date in MM/DD/YYYY format',
      Input: (val: string) => {
        if (isEmptyOrNull(val)) return;
        handleDatePickerInput(input as HTMLInputElement, val);
      }
    };
  });
}

/**
 * Set only while readSectionForFocusCapture runs. The three extractors
 * look up the same containers, the DOM cannot change during that synchronous
 * read, and these lookups were nearly all of its cost.
 */
let sectionNamesForCapture: Map<Element, string> | null = null;

function findSectionNameForContainer(elementContainer: Element): string {
  if (!sectionNamesForCapture) {
    return findSectionNameForContainerUncached(elementContainer);
  }
  let sectionName = sectionNamesForCapture.get(elementContainer);
  if (sectionName === undefined) {
    sectionName = findSectionNameForContainerUncached(elementContainer);
    sectionNamesForCapture.set(elementContainer, sectionName);
  }
  return sectionName;
}

// flex checkbox questions handling
// Helper function to find section name for an element container
function findSectionNameForContainerUncached(elementContainer: Element): string {
  const knownSectionNames = [
    'Subjective',
    'Objective',
    'Assessment',
    'Plan',
    'Flowsheet'
  ];

  // First, try to find section header by looking backwards through siblings
  let current: Element | null = elementContainer.previousElementSibling;
  let depth = 0;

  while (current && depth < 10) {
    const text = current.textContent?.trim();
    if (text && knownSectionNames.includes(text)) {
      // Check if this looks like a section header (has header class or is a direct child of a section container)
      if (
        current.className.includes('header') ||
        current.querySelector('[class*="elementContainer"]') ||
        current.parentElement?.querySelector('[class*="elementContainer"]')
      ) {
        return text;
      }
    }
    current = current.previousElementSibling;
    depth++;
  }

  // Also check parent siblings
  let parent = elementContainer.parentElement;
  while (parent && parent !== document.body) {
    const siblings = Array.from(parent.children);
    const containerIndex = siblings.indexOf(elementContainer);
    for (let i = containerIndex - 1; i >= 0 && i >= containerIndex - 5; i--) {
      const siblingText = siblings[i].textContent?.trim();
      if (siblingText && knownSectionNames.includes(siblingText)) {
        if (
          siblings[i].className.includes('header') ||
          siblings[i].querySelector('[class*="elementContainer"]')
        ) {
          return siblingText;
        }
      }
    }
    parent = parent.parentElement;
  }

  // Look for section headers in the document and find the correct one based on document position
  const allHeaders: Array<{ name: string; element: Element }> = [];

  // Collect all section headers with their positions
  for (const sectionName of knownSectionNames) {
    const headers = Array.from(document.querySelectorAll('div')).filter(
      (div) => {
        const text = div.textContent?.trim();
        return (
          text === sectionName &&
          (div.className.includes('header') ||
            div.closest('[class*="elementContainer"]')
              ?.previousElementSibling === div ||
            div.parentElement?.querySelector('[class*="elementContainer"]'))
        );
      }
    );

    headers.forEach((header) => {
      allHeaders.push({ name: sectionName, element: header });
    });
  }

  // Sort headers by document position
  allHeaders.sort((a, b) => {
    const position = a.element.compareDocumentPosition(b.element);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
      return -1; // a comes before b
    } else if (position & Node.DOCUMENT_POSITION_PRECEDING) {
      return 1; // a comes after b
    }
    return 0;
  });

  // Find the section header that comes immediately before this element container
  // and check if the element is within that section's boundaries
  for (let i = allHeaders.length - 1; i >= 0; i--) {
    const header = allHeaders[i];
    const headerPosition =
      header.element.compareDocumentPosition(elementContainer);

    // Check if element container comes after this header
    if (headerPosition & Node.DOCUMENT_POSITION_FOLLOWING) {
      // Check if there's a next section header, and if so, verify container comes before it
      const nextHeader = allHeaders[i + 1];
      if (!nextHeader) {
        // This is the last section, element must belong to it
        let sectionContainer = header.element.parentElement;
        while (sectionContainer && sectionContainer !== document.body) {
          if (sectionContainer.contains(elementContainer)) {
            return header.name;
          }
          sectionContainer = sectionContainer.parentElement;
        }
      } else {
        const nextPosition =
          nextHeader.element.compareDocumentPosition(elementContainer);
        // If container comes before next header, it belongs to current section
        if (nextPosition & Node.DOCUMENT_POSITION_PRECEDING) {
          let sectionContainer = header.element.parentElement;
          while (sectionContainer && sectionContainer !== document.body) {
            if (sectionContainer.contains(elementContainer)) {
              return header.name;
            }
            sectionContainer = sectionContainer.parentElement;
          }
        }
      }
    }
  }

  // Fallback: check if element is near/within a table (typically Objective)
  if (
    elementContainer.closest('table') ||
    elementContainer.querySelector('table')
  ) {
    // Check if there's an Objective header
    const objectiveHeaders = allHeaders.filter((h) => h.name === 'Objective');
    if (objectiveHeaders.length > 0) {
      return 'Objective';
    }
  }

  return 'Unknown';
}

function extractCheckboxes() {
  // Find element containers (same structure as extractTextInputsAndTextareas uses)
  const elementContainers = queryNote('[class*="elementContainer"]');

  if (elementContainers.length === 0) return;

  elementContainers.forEach((elementContainer) => {
    // Find section name using helper function
    const sectionName = findSectionNameForContainer(elementContainer);
    const sanitizedSectionName = sanitizeKey(sectionName);
    if (sanitizedSectionName === 'Objective') return;

    // Find subsection title
    const subsectionTitleEl = elementContainer.querySelector(
      '[class*="templateSubsectionTitle"] .MuiBox-root'
    );
    const subSectionLabel =
      subsectionTitleEl?.textContent?.trim() || 'Untitled';
    const sanitizedSubSectionLabel = sanitizeKey(subSectionLabel);

    if (!questionInteractions[sanitizedSectionName]) {
      questionInteractions[sanitizedSectionName] = {
        Type: 'Flex',
        Key: sanitizedSectionName,
        Question: sanitizedSectionName,
        Structure: {},
        SubsectionElements: {},
        Elements: []
      };
    }

    if (
      !questionInteractions[sanitizedSectionName].Structure[
        sanitizedSubSectionLabel
      ]
    ) {
      questionInteractions[sanitizedSectionName].Structure[
        sanitizedSubSectionLabel
      ] = {};
      questionInteractions[sanitizedSectionName].SubsectionElements[
        sanitizedSubSectionLabel
      ] = elementContainer;
    }

    // Find checkboxes - updated for MUI structure
    const checkboxes = elementContainer.querySelectorAll(
      'input[type="checkbox"]:not([aria-hidden="true"])'
    );

    if (checkboxes.length === 0) return;

    // Group checkboxes by their closest h6 heading (if present)
    // This allows "Aggravating Factors" and "Easing Factors" to be separate groups
    const checkboxGroups = new Map<string, HTMLInputElement[]>();

    Array.from(checkboxes).forEach((checkbox) => {
      const checkboxEl = checkbox as HTMLInputElement;

      // Find the h6 heading that groups this checkbox
      let groupKey = subSectionLabel; // Default to subsection label

      // Look for h6 heading in the same parent container as the checkbox
      // The h6 and checkboxes are typically siblings under a common parent
      let parent = checkboxEl.closest('.MuiBox-root');
      while (parent && parent !== elementContainer) {
        // Check if there's an h6 in this parent or a sibling
        const h6InParent = parent.querySelector('h6.MuiTypography-h6');
        if (h6InParent) {
          const h6Text = h6InParent.textContent?.trim();
          if (h6Text) {
            groupKey = h6Text;
            break;
          }
        }

        // Also check previous siblings for h6
        let sibling = parent.previousElementSibling;
        while (sibling) {
          const h6InSibling = sibling.querySelector('h6.MuiTypography-h6');
          if (h6InSibling) {
            const h6Text = h6InSibling.textContent?.trim();
            if (h6Text) {
              groupKey = h6Text;
              break;
            }
          }
          sibling = sibling.previousElementSibling;
        }

        if (groupKey !== subSectionLabel) break;

        parent = parent.parentElement?.closest('.MuiBox-root') || null;
      }

      // If still no h6 found, look for the closest h6 that comes before this checkbox
      if (groupKey === subSectionLabel) {
        const allH6s = elementContainer.querySelectorAll('h6.MuiTypography-h6');
        let closestH6: HTMLElement | null = null;

        for (const h6 of Array.from(allH6s)) {
          const h6Element = h6 as HTMLElement;
          // Check if this h6 comes before the checkbox in document order
          const position = h6Element.compareDocumentPosition(checkboxEl);
          if (position & Node.DOCUMENT_POSITION_PRECEDING) {
            // h6 comes before checkbox, check if it's the closest one
            if (
              !closestH6 ||
              closestH6.compareDocumentPosition(h6Element) &
                Node.DOCUMENT_POSITION_PRECEDING
            ) {
              closestH6 = h6Element;
            }
          }
        }

        if (closestH6) {
          const h6Text = closestH6.textContent?.trim();
          if (h6Text) {
            groupKey = h6Text;
          }
        }
      }

      // Clean up the group key
      groupKey = groupKey
        .replace(/\b(\w+)\b\s+\1\b/gi, '$1')
        .replace(/\bLocation\b$/i, '')
        .trim();

      if (!checkboxGroups.has(groupKey)) {
        checkboxGroups.set(groupKey, []);
      }
      checkboxGroups.get(groupKey)!.push(checkboxEl);
    });

    // Process each group
    for (const [groupLabel, groupCheckboxes] of Array.from(
      checkboxGroups.entries()
    )) {
      const optionLabel = (box: HTMLInputElement) => {
        const labelElement = box.closest('label.MuiFormControlLabel-root');
        if (labelElement) {
          const labelSpan = labelElement.querySelector(
            '.MuiFormControlLabel-label span, .MuiFormControlLabel-label'
          );
          const labelText =
            labelSpan?.textContent?.trim() ||
            labelElement.textContent?.trim();
          return labelText
            .replace(/CheckBoxOutlineBlankIcon|CheckBoxIcon/g, '')
            .trim();
        }
        return box.value || 'Option';
      };
      const options = groupCheckboxes.map(optionLabel).filter(Boolean);

      // Use the group label (h6 heading or subsection label) as the key
      questionInteractions[sanitizedSectionName].Structure[
        sanitizedSubSectionLabel
      ][groupLabel] = {
        ...(capturingValues
          ? {
              'Existing Value': groupCheckboxes
                .filter((box) => box.checked)
                .map(optionLabel)
                .filter(Boolean)
                .join('$')
            }
          : {}),
        Description: `Response will be from the following options: ${options.join(', ')}. Give '$' separated values as response.`,
        Input: (option, checked = true) => {
          if (isEmptyOrNull(option)) return;
          // Handle multiple options separated by $
          const optionsToCheck =
            typeof option === 'string'
              ? option
                  .split('$')
                  .map((o) => o.trim())
                  .filter(Boolean)
              : [option];

          optionsToCheck.forEach((opt) => {
            const target = groupCheckboxes.find((cb) => {
              const labelElement = cb.closest('label.MuiFormControlLabel-root');
              if (labelElement) {
                const labelSpan = labelElement.querySelector(
                  '.MuiFormControlLabel-label span, .MuiFormControlLabel-label'
                );
                const labelText =
                  labelSpan?.textContent?.trim() ||
                  labelElement.textContent?.trim();
                const cleanLabel = labelText
                  .replace(/CheckBoxOutlineBlankIcon|CheckBoxIcon/g, '')
                  .trim();
                return (
                  cleanLabel.toLowerCase() === opt.toString().toLowerCase() ||
                  cleanLabel
                    .toLowerCase()
                    .includes(opt.toString().toLowerCase()) ||
                  opt
                    .toString()
                    .toLowerCase()
                    .includes(cleanLabel.toLowerCase())
                );
              }
              return false;
            });
            if (target) handleCheckboxInput(target, checked);
          });
        }
      };
    }

    if (
      !questionInteractions[sanitizedSectionName].Elements.includes(
        elementContainer
      )
    ) {
      questionInteractions[sanitizedSectionName].Elements.push(
        elementContainer
      );
    }
  });
}

function extractObjectiveTable() {
  if (!questionInteractions['Objective']) {
    questionInteractions['Objective'] = {
      Key: 'Objective',
      Question: 'Objective',
      Type: 'Flex',
      Structure: {},
      SubsectionElements: {},
      Elements: []
    };
  }
  const allTables = queryNote('table');
  let i = 0;

  allTables.forEach((table) => {
    const mainHeader = getMainHeaderForTable(table) || 'Screening';
    const firstTh =
      table.querySelector('thead th') || table.querySelector('tbody tr td');
    const subsectionLabel = firstTh?.textContent?.trim() || 'Untitled';
    const compositeKey = `${mainHeader} - ${subsectionLabel}`;
    const rows = table.querySelectorAll('tbody tr');
    rows.forEach((row) => {
      const cells = row.querySelectorAll('td');
      if (cells.length === 0) return;

      const rowLabel = cells[0]?.textContent?.trim();
      if (!rowLabel) return;

      cells.forEach((cell, index) => {
        if (index === 0) return;

        let suffix;
        if (cells.length === 3) {
          suffix =
            index === 1 ? 'Left Side' : index === 2 ? 'Right Side' : null;
        } else if (cells.length === 2) {
          const isScreening = /screening|central|posture|trunk|cervical/i.test(
            mainHeader
          );
          suffix = index === 1 ? (isScreening ? 'Central' : 'Left Side') : null;
        } else {
          suffix = `Col${index}`;
        }
        if (!suffix) return;

        const fullLabel = `${subsectionLabel} - ${rowLabel} - ${suffix}`;

        // Ensure Structure[compositeKey] is initialized
        if (!questionInteractions['Objective'].Structure[compositeKey]) {
          questionInteractions['Objective'].Structure[compositeKey] = {};
          questionInteractions['Objective'].SubsectionElements[compositeKey] =
            table;
        }

        // ROM-style fields (AROM, PROM, Pain checkbox) - updated for MUI
        const labels = cell.querySelectorAll('label.MuiFormLabel-root, label');
        let handledROM = false;

        labels.forEach((labelEl) => {
          const labelText = labelEl.textContent?.trim();
          // Look for input near the label - check next sibling or within the same container
          const labelContainer =
            labelEl.closest('.MuiFormControl-root') || labelEl.parentElement;
          const input = labelContainer?.querySelector(
            "input[type='text']:not([aria-autocomplete='list']):not([aria-hidden='true']), .MuiInputBase-input[type='text']:not([aria-autocomplete='list']):not([aria-hidden='true'])"
          ) as HTMLInputElement;
          const checkbox =
            (labelEl.querySelector(
              "input[type='checkbox']:not([aria-hidden='true'])"
            ) as HTMLInputElement) ||
            (labelContainer?.querySelector(
              "input[type='checkbox']:not([aria-hidden='true'])"
            ) as HTMLInputElement);

          if (labelText === 'AROM' && input) {
            const fullLabelAROM = `${fullLabel} - AROM`;
            questionInteractions['Objective'].Structure[compositeKey][
              fullLabelAROM
            ] = {
              'Existing Value': input.value,
              Input: (val, replyType = 'overwrite') => {
                const el =
                  resolveROMTableInput(compositeKey, fullLabelAROM) || input;
                if (el) handleTextInput(el, val, replyType);
              }
            };
            handledROM = true;
          }

          if (labelText === 'PROM' && input) {
            const fullLabelPROM = `${fullLabel} - PROM`;
            questionInteractions['Objective'].Structure[compositeKey][
              fullLabelPROM
            ] = {
              'Existing Value': input.value,
              Input: (val, replyType = 'overwrite') => {
                const el =
                  resolveROMTableInput(compositeKey, fullLabelPROM) || input;
                if (el) handleTextInput(el, val, replyType);
              }
            };
            handledROM = true;
          }

          if (labelText === 'Pain' && checkbox) {
            const fullLabelPain = `${fullLabel} - Pain`;
            questionInteractions['Objective'].Structure[compositeKey][
              fullLabelPain
            ] = {
              ...(capturingValues
                ? { 'Existing Value': checkbox.checked ? 'Yes' : 'No' }
                : {}),
              Input: (val) => {
                const el =
                  resolveROMTableInput(compositeKey, fullLabelPain) || checkbox;
                if (el && val) handleCheckboxInput(el, true);
              }
            };
            handledROM = true;
          }
        });

        if (handledROM) return;

        // Toggle buttons - updated for MUI
        const toggleGroup = cell.querySelector(
          '[role="group"][class*="MuiToggleButtonGroup-root"]'
        ) as HTMLElement;
        if (toggleGroup) {
          const buttons = toggleGroup.querySelectorAll('button');
          let description = '';
          let choices = Array.from(buttons)
            .map((btn) => btn.textContent?.trim())
            .filter(Boolean);

          if (choices.length > 0) {
            if (choices.includes('Positive') && choices.includes('Negative')) {
              description =
                'Response will be from the following options: Positive, Negative, None.';
              choices = [];
            }
            questionInteractions['Objective'].Structure[compositeKey][
              `${fullLabel}`
            ] = {
              ...(capturingValues
                ? { 'Existing Value': getPressedToggleLabel(toggleGroup) }
                : {}),
              ...(description &&
                description.length > 0 && { Description: description }),
              ...(choices && choices.length > 0 && { Choices: choices }),
              Input: (option) => {
                if (!option) return;
                handleSingleChoiceToggle(toggleGroup, option);
              }
            };
            return;
          }
        }

        // Handle number inputs with sliders
        const numberInput = cell.querySelector(
          "input[type='number']:not([readonly]):not([aria-hidden='true'])"
        ) as HTMLInputElement;
        if (numberInput) {
          questionInteractions['Objective'].Structure[compositeKey][
            `${fullLabel}`
          ] = {
            ...(capturingValues ? { 'Existing Value': numberInput.value } : {}),
            Type: 'Integer',
            Description: 'Value should be a integer',
            Input: (val) => {
              if (isEmptyOrNull(val)) return;
              const numVal = parseFloat(val.toString());
              if (!isNaN(numVal)) {
                const next = numVal.toString();
                if (
                  numberInput.value !== next &&
                  !(numberInput as any).__updating
                ) {
                  // Use queue system for Objective table
                  updateQueue.push(() => {
                    if ((numberInput as any).__updating) return;
                    (numberInput as any).__updating = true;
                    setNativeValue(numberInput, next);
                    setTimeout(() => {
                      try {
                        numberInput.dispatchEvent(
                          new Event('input', {
                            bubbles: true,
                            cancelable: true
                          })
                        );
                      } finally {
                        delete (numberInput as any).__updating;
                      }
                    }, 100);
                  });

                  // Also update slider if present
                  const slider = numberInput
                    .closest('.MuiFormControl-root')
                    ?.querySelector(
                      'input[type="range"]'
                    ) as HTMLInputElement | null;
                  if (
                    slider &&
                    slider.value !== next &&
                    !(slider as any).__updating
                  ) {
                    updateQueue.push(() => {
                      if ((slider as any).__updating) return;
                      (slider as any).__updating = true;
                      setNativeValue(slider, next);
                      setTimeout(() => {
                        try {
                          slider.dispatchEvent(
                            new Event('input', {
                              bubbles: true,
                              cancelable: true
                            })
                          );
                        } finally {
                          delete (slider as any).__updating;
                        }
                      }, 100);
                    });
                  }

                  processUpdateQueue();
                }
              }
            }
          };
          return;
        }

        // Handle text inputs (e.g. Strength table)
        const textInput = cell.querySelector(
          "input[type='text']:not([readonly]):not([aria-hidden='true']):not([aria-autocomplete='list']), .MuiInputBase-input[type='text']:not([readonly]):not([aria-hidden='true']):not([aria-autocomplete='list'])"
        ) as HTMLInputElement;
        if (textInput) {
          questionInteractions['Objective'].Structure[compositeKey][
            `${fullLabel}`
          ] = {
            'Existing Value': textInput.value,
            Input: (val, replyType = 'overwrite') => {
              const el =
                resolveTableTextOrTextarea(compositeKey, fullLabel) ||
                textInput;
              if (el) handleTextInput(el, val, replyType);
            }
          };
          return;
        }

        // Handle textareas (e.g. Strength table)
        const textarea = cell.querySelector(
          "textarea:not([aria-hidden='true']):not([readonly]), .MuiInputBase-input.MuiInputBase-inputMultiline:not([aria-hidden='true']):not([readonly])"
        ) as HTMLTextAreaElement;
        if (textarea) {
          questionInteractions['Objective'].Structure[compositeKey][
            `${fullLabel}`
          ] = {
            'Existing Value': textarea.value,
            Input: (val, replyType = 'overwrite') => {
              const el =
                resolveTableTextOrTextarea(compositeKey, fullLabel) || textarea;
              if (el) handleTextareaInput(el, val, replyType);
            }
          };
          return;
        }

        // Handle checkboxes (non-ROM)
        const checkbox = cell.querySelector(
          "input[type='checkbox']:not([aria-hidden='true'])"
        ) as HTMLInputElement;
        if (checkbox) {
          const labelElement = checkbox.closest(
            'label.MuiFormControlLabel-root'
          );
          const checkboxLabel = labelElement
            ? (
                labelElement.querySelector(
                  '.MuiFormControlLabel-label span, .MuiFormControlLabel-label'
                ) as HTMLElement
              )?.textContent?.trim() || labelElement.textContent?.trim()
            : 'Checkbox';

          questionInteractions['Objective'].Structure[compositeKey][
            `${fullLabel}`
          ] = {
            ...(capturingValues
              ? { 'Existing Value': checkbox.checked ? 'Yes' : 'No' }
              : {}),
            Input: (val) => {
              if (isEmptyOrNull(val)) return;
              const shouldCheck =
                val === true ||
                val === 'true' ||
                val === 'Yes' ||
                val === 'yes';
              handleCheckboxInput(checkbox, shouldCheck);
            }
          };
          return;
        }
      });
    });
  });
}

function extractFlowsheet() {
  if (!questionInteractions['Flowsheet']) {
    questionInteractions['Flowsheet'] = {
      Key: 'Flowsheet',
      Question: 'Flowsheet',
      Type: 'Flex',
      Structure: {},
      SubsectionElements: {},
      Elements: []
    };
  }

  const accordionSections = queryNote('[id^="flowsheet-procedure-"]');

  accordionSections.forEach((section) => {
    const titleEl = section.querySelector(
      'div[class*="MuiBox-root"] > div[class*="MuiBox-root"]'
    );
    const procedureName = titleEl?.innerText?.trim() || 'Untitled Procedure';

    const treatmentBlocks = section.querySelectorAll(
      '.formBody[data-rbd-droppable-id] > div'
    );

    treatmentBlocks.forEach((block, index) => {
      const blockLabel = `${procedureName}${index + 1}`;
      const structure = (questionInteractions['Flowsheet'].Structure[
        blockLabel
      ] = {});
      questionInteractions['Flowsheet'].SubsectionElements[blockLabel] = block;

      // Treatment textarea
      const treatmentInput = block.querySelector(
        'textarea[placeholder="Treatment"]'
      );
      if (treatmentInput) {
        structure['Treatment'] = {
          'Existing Value': treatmentInput.value,
          Input: (val, replyType = 'overwrite') =>
            handleTextareaInput(treatmentInput, val, replyType)
        };
      }

      // Measurement textarea
      const measurementInput = block.querySelector(
        'label[for*="procedures"] + div textarea'
      );
      if (measurementInput) {
        structure['Measurement'] = {
          'Existing Value': measurementInput.value,
          Input: (val, replyType = 'overwrite') =>
            handleTextareaInput(measurementInput, val, replyType)
        };
      }

      // Performed checkbox
      const checkbox = block.querySelector('input[type="checkbox"]');
      if (checkbox) {
        structure['Performed'] = {
          ...(capturingValues
            ? { 'Existing Value': checkbox.checked ? 'Yes' : 'No' }
            : {}),
          Choices: ['Yes', 'No'],
          Input: (val) => {
            const checked = checkbox.checked;
            if (val === 'Yes' && !checked) checkbox.click();
            if (val === 'No' && checked) checkbox.click();
          }
        };
      }
    });
  });
}

// individual checkbox questions handling
function getCheckboxQuestionsStructured() {
  const allBoxes = document.querySelectorAll('.MuiBox-root-290');
  const sectionInstanceMap = new Map();
  const sectionCountMap = {};
  const groupTracker = {};
  const ignoredLabels = ['Best', 'Common'];

  allBoxes.forEach((box) => {
    const checkboxes = box.querySelectorAll('input[type="checkbox"]');
    if (!checkboxes.length) return;

    // Try to get sub-label from known span
    let subLabel = box.querySelector('div.jss665 > span')?.textContent?.trim();

    // Fallback: search upward for jss1239
    if (!subLabel) {
      let parent = box.parentElement;
      while (parent && !subLabel) {
        const fallback = parent.querySelector('.MuiBox-root-290.jss1239');
        subLabel = fallback?.textContent?.trim();
        parent = parent.parentElement;
      }
    }

    if (!subLabel) subLabel = 'Untitled';
    if (ignoredLabels.includes(subLabel)) return;

    // Section Info
    const sectionWrapper = box.closest('[name^="data-"]');
    if (!sectionWrapper) return;

    const sectionTitleEl = Array.from(
      sectionWrapper.querySelectorAll('.MuiBox-root-290')
    ).find((el) => el.textContent?.trim());
    const sectionTitle =
      sectionTitleEl?.textContent?.trim() || 'Unknown Section';
    if (ignoredLabels.includes(sectionTitle)) return;

    // Track section instances
    if (!sectionInstanceMap.has(sectionWrapper)) {
      const currentCount = (sectionCountMap[sectionTitle] || 0) + 1;
      sectionCountMap[sectionTitle] = currentCount;
      sectionInstanceMap.set(sectionWrapper, `${sectionTitle}`);
    }

    const sectionKey = sectionInstanceMap.get(sectionWrapper);
    const groupKeyBase = `${sectionKey} - ${subLabel} - Check Box`;
    let finalLabel = groupKeyBase;
    const sanitizedFinalLabel = sanitizeKey(finalLabel);
    // Handle duplicate subgroup headers
    if (!groupTracker[groupKeyBase]) {
      groupTracker[groupKeyBase] = 1;
    } else {
      groupTracker[groupKeyBase]++;
      finalLabel = `${groupKeyBase} (${groupTracker[groupKeyBase]})`;
    }

    // Process each checkbox within the box
    checkboxes.forEach((checkbox) => {
      const labelEl = checkbox.closest('label');
      const spans = labelEl?.querySelectorAll('span') || [];
      const choiceLabel = spans[spans.length - 1]?.textContent?.trim();

      if (!choiceLabel) return;
      const sanitizedChoiceLabel = sanitizeKey(choiceLabel);
      // Initialize group if not exists
      if (!questionInteractions[sanitizedFinalLabel]) {
        questionInteractions[sanitizedFinalLabel] = {
          Key: sanitizedFinalLabel,
          Question: sanitizedFinalLabel,
          Type: 'Choose All',
          Choices: [],
          ExistingNote: [],
          Elements: box,
          Input: (selectedOptions) => {
            if (isEmptyOrNull(selectedOptions)) return;
            if (!Array.isArray(selectedOptions)) return;
            document
              .querySelectorAll('input[type="checkbox"]')
              .forEach((boxEl) => {
                const lbl = boxEl.closest('label')?.querySelectorAll('span');
                const boxLabel = lbl?.[lbl.length - 1]?.textContent?.trim();
                const shouldBeChecked = selectedOptions.includes(boxLabel);
                if (shouldBeChecked && !boxEl.checked) boxEl.click();
              });
          }
        };
      }

      // Add the label if not already present
      if (
        !questionInteractions[sanitizedFinalLabel].Choices.includes(
          sanitizedChoiceLabel
        )
      ) {
        questionInteractions[sanitizedFinalLabel].Choices.push(
          sanitizedChoiceLabel
        );
      }
    });
  });

  return questionInteractions;
}

function extractSingleChoiceToggles() {
  // Find element containers (same structure as extractTextInputsAndTextareas uses)
  const elementContainers = queryNote('[class*="elementContainer"]');

  if (elementContainers.length === 0) return;

  elementContainers.forEach((elementContainer) => {
    // Find section name using helper function
    const sectionName = findSectionNameForContainer(elementContainer);
    const sanitizedSectionName = sanitizeKey(sectionName);
    if (sanitizedSectionName === 'Objective') return;

    // Find subsection title
    const subsectionTitleEl = elementContainer.querySelector(
      '[class*="templateSubsectionTitle"] .MuiBox-root'
    );
    const subSectionLabel =
      subsectionTitleEl?.textContent?.trim() || 'Untitled';
    const sanitizedSubSectionLabel = sanitizeKey(subSectionLabel);

    if (!questionInteractions[sanitizedSectionName]) {
      questionInteractions[sanitizedSectionName] = {
        Type: 'Flex',
        Key: sanitizedSectionName,
        Question: sanitizedSectionName,
        Structure: {},
        SubsectionElements: {},
        Elements: []
      };
    }

    if (
      !questionInteractions[sanitizedSectionName].Structure[
        sanitizedSubSectionLabel
      ]
    ) {
      questionInteractions[sanitizedSectionName].Structure[
        sanitizedSubSectionLabel
      ] = {};
      questionInteractions[sanitizedSectionName].SubsectionElements[
        sanitizedSubSectionLabel
      ] = elementContainer;
    }

    const labelCounts = {};

    const toggleGroups = elementContainer.querySelectorAll(
      '[role="group"][class*="MuiToggleButtonGroup-root"]'
    );

    toggleGroups.forEach((group, groupIndex) => {
      const buttons = group.querySelectorAll('button');
      const options = Array.from(buttons).map(
        (btn) =>
          btn.textContent?.trim().replace(/\s+/g, ' ') ||
          `Option ${groupIndex + 1}`
      );

      const baseLabel = generateSmartLabel(
        elementContainer,
        subSectionLabel,
        group
      );
      labelCounts[baseLabel] = (labelCounts[baseLabel] || 0) + 1;
      const label =
        labelCounts[baseLabel] > 1
          ? `${baseLabel} (${labelCounts[baseLabel]})`
          : baseLabel;
      const sanitizedLabel = sanitizeKey(label);
      questionInteractions[sanitizedSectionName].Structure[
        sanitizedSubSectionLabel
      ][sanitizedLabel] = {
        ...(capturingValues
          ? { 'Existing Value': getPressedToggleLabel(group) }
          : {}),
        Choices: options,
        Input: (option) => {
          if (isEmptyOrNull(option)) return;
          handleSingleChoiceToggle(group, option);
        }
      };
    });

    if (
      !questionInteractions[sanitizedSectionName].Elements.includes(
        elementContainer
      )
    ) {
      questionInteractions[sanitizedSectionName].Elements.push(
        elementContainer
      );
    }
  });
}

function generateSmartLabel(sectionElement, subSectionLabel, inputElement) {
  const isTextareaLike =
    inputElement.tagName?.toLowerCase() === 'textarea' ||
    inputElement.classList?.contains('MuiInputBase-inputMultiline');

  // First, try to find the label directly associated with this input
  // Check if input has an id and find label with matching 'for' attribute
  const inputId = inputElement.getAttribute('id');
  if (inputId) {
    // First try within the same form control (most specific)
    const formControl = inputElement.closest('.MuiFormControl-root');
    if (formControl) {
      const associatedLabel = formControl.querySelector(
        `label[for="${inputId}"]`
      );
      if (associatedLabel) {
        const labelText = associatedLabel.textContent?.trim();
        if (labelText && labelText !== '\u200B') {
          return labelText;
        }
      }
    }
    // Fallback to document-wide search (for backwards compatibility)
    const associatedLabel = document.querySelector(`label[for="${inputId}"]`);
    if (associatedLabel) {
      const labelText = associatedLabel.textContent?.trim();
      if (labelText && labelText !== '\u200B') {
        return labelText;
      }
    }
  }

  // Check if input is inside a label element
  const parentLabel = inputElement.closest('label');
  if (parentLabel) {
    const labelText = parentLabel.textContent?.trim();
    if (labelText && labelText !== '\u200B') {
      // Remove the input value from label text if present
      const cleaned = labelText.replace(inputElement.value || '', '').trim();
      if (cleaned) return cleaned;
    }
  }

  // Check the closest form control for a label
  const formControl = inputElement.closest('.MuiFormControl-root');
  if (formControl) {
    const formLabel = formControl.querySelector(
      'label.MuiFormLabel-root, label.MuiInputLabel-root'
    );
    if (formLabel) {
      const labelText = formLabel.textContent?.trim();
      if (labelText && labelText !== '\u200B') {
        return labelText;
      }
    }
  }

  // Fallback: traverse up the DOM tree
  const parts = new Set();
  let current = inputElement.closest('[class^="MuiBox-root"]');

  while (current && current !== sectionElement) {
    const h6 = current.querySelector('h6')?.textContent?.trim();
    const span = current.querySelector('span')?.textContent?.trim();
    const label1 = current.querySelector('label')?.textContent?.trim();

    if (h6 && h6 !== '\u200B') parts.add(h6);
    else if (span && span !== '\u200B') parts.add(span);
    else if (label1 && label1 !== '\u200B') parts.add(label1);

    current = current.parentElement;
  }

  const label = [...parts]
    .filter(Boolean)
    .map((s) => s.trim())
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  // For textarea-like fields that don't have an explicit label, prefer the
  // subsection label instead of accidentally picking up a sibling field's label
  // (e.g. avoiding using "Treatment Diagnosis" for the Subjective Overview textarea).
  if (!label || isTextareaLike) {
    return subSectionLabel;
  }

  return label;
}

// Handlers

// Global update queue to prevent concurrent updates across elements (prevents React error #185)
let updateQueue: Array<() => void> = [];
let isProcessingQueue = false;

function processUpdateQueue() {
  if (isProcessingQueue || updateQueue.length === 0) return;
  isProcessingQueue = true;

  const update = updateQueue.shift();
  if (update) {
    update();
    // Process next update after delay
    setTimeout(() => {
      isProcessingQueue = false;
      processUpdateQueue();
    }, 50);
  } else {
    isProcessingQueue = false;
  }
}

/** Find column index by matching suffix to header row cell text (case-insensitive). */
function getColIndexForSuffix(table: Element, suffix: string): number {
  const headerRow = table.querySelector('tbody tr');
  if (!headerRow) return -1;
  const normalizedSuffix = suffix.trim().toLowerCase();
  const cells = headerRow.querySelectorAll('td');
  for (let i = 1; i < cells.length; i++) {
    const cellText = cells[i]?.textContent?.trim().toLowerCase();
    if (cellText === normalizedSuffix) return i;
  }
  return -1;
}

/**
 * Resolve ROM table input at apply-time to avoid stale refs after React re-renders.
 * Returns the current DOM element for the given compositeKey and fullLabel.
 */
function resolveROMTableInput(
  compositeKey: string,
  fullLabel: string
): HTMLInputElement | HTMLTextAreaElement | null {
  const parts = fullLabel.split(' - ');
  if (parts.length < 4) return null;
  const rowLabel = parts[1];
  const suffix = parts[2];
  const type = parts[3];

  const tables = Array.from(document.querySelectorAll('table'));
  for (const table of tables) {
    const firstCell =
      table.querySelector('thead th') || table.querySelector('tbody tr td');
    const subsectionLabel = firstCell?.textContent?.trim() || '';
    const mainHeader = getMainHeaderForTable(table);
    const key = `${mainHeader} - ${subsectionLabel}`;
    if (key.trim().toLowerCase() !== compositeKey.trim().toLowerCase())
      continue;

    const colIndex = getColIndexForSuffix(table, suffix);
    if (colIndex < 0) continue;

    const normalizedRowLabel = rowLabel.trim().toLowerCase();
    const rows = Array.from(table.querySelectorAll('tbody tr'));
    for (const row of rows) {
      const cells = row.querySelectorAll('td');
      if (cells.length <= colIndex) continue;
      const firstCellText = cells[0]?.textContent?.trim().toLowerCase();
      if (firstCellText !== normalizedRowLabel) continue;

      const cell = cells[colIndex];
      if (!cell) continue;

      const labels = Array.from(
        cell.querySelectorAll('label.MuiFormLabel-root, label')
      );
      for (const labelEl of labels) {
        const labelText = labelEl.textContent?.trim();
        if (labelText !== type) continue;
        const container =
          labelEl.closest('.MuiFormControl-root') || labelEl.parentElement;
        if (!container) continue;
        if (type === 'Pain') {
          const cb = container.querySelector(
            "input[type='checkbox']:not([aria-hidden='true'])"
          ) as HTMLInputElement;
          return cb;
        }
        const input = container.querySelector(
          "input[type='text']:not([aria-autocomplete='list']):not([aria-hidden='true']), .MuiInputBase-input[type='text']:not([aria-autocomplete='list']):not([aria-hidden='true'])"
        ) as HTMLInputElement;
        return input;
      }
    }
  }
  return null;
}

/**
 * Resolve table text/textarea input at apply-time (e.g. Strength table).
 * fullLabel format: "Shoulder - Flexion - Left Side" (subsectionLabel - rowLabel - suffix).
 */
function resolveTableTextOrTextarea(
  compositeKey: string,
  fullLabel: string
): HTMLInputElement | HTMLTextAreaElement | null {
  const parts = fullLabel.split(' - ');
  if (parts.length < 3) return null;
  const rowLabel = parts[1];
  const suffix = parts[2];

  const tables = Array.from(document.querySelectorAll('table'));
  for (const table of tables) {
    const firstCell =
      table.querySelector('thead th') || table.querySelector('tbody tr td');
    const subsectionLabel = firstCell?.textContent?.trim() || '';
    const mainHeader = getMainHeaderForTable(table);
    const key = `${mainHeader} - ${subsectionLabel}`;
    if (key.trim().toLowerCase() !== compositeKey.trim().toLowerCase())
      continue;

    const colIndex = getColIndexForSuffix(table, suffix);
    if (colIndex < 0) continue;

    const normalizedRowLabel = rowLabel.trim().toLowerCase();
    const rows = Array.from(table.querySelectorAll('tbody tr'));
    for (const row of rows) {
      const cells = row.querySelectorAll('td');
      if (cells.length <= colIndex) continue;
      const firstCellText = cells[0]?.textContent?.trim().toLowerCase();
      if (firstCellText !== normalizedRowLabel) continue;

      const cell = cells[colIndex];
      const textarea = cell.querySelector(
        "textarea:not([aria-hidden='true']):not([readonly]), .MuiInputBase-input.MuiInputBase-inputMultiline:not([aria-hidden='true']):not([readonly])"
      ) as HTMLTextAreaElement;
      if (textarea) return textarea;

      const textInput = cell.querySelector(
        "input[type='text']:not([readonly]):not([aria-hidden='true']):not([aria-autocomplete='list']), .MuiInputBase-input[type='text']:not([readonly]):not([aria-hidden='true']):not([aria-autocomplete='list'])"
      ) as HTMLInputElement;
      if (textInput) return textInput;
    }
  }
  return null;
}

function getMainHeaderForTable(element: Element): string {
  const headers: string[] = [];
  let current: Element | null = element;

  while (current && headers.length < 3) {
    const box = current.querySelector?.("div[class*='MuiBox-root']");
    if (box && box.textContent?.trim()) {
      const label = box.textContent.trim();
      if (!headers.includes(label) && /^[A-Za-z]/.test(label)) {
        headers.unshift(label);
      }
    }
    current = current.parentElement;
  }

  const filtered = headers.filter(
    (h) => !/body/i.test(h) && !/objective/i.test(h)
  );
  if (headers.length === 3) {
    filtered.splice(1, 1);
  }
  return filtered.join(' - ');
}

function setNativeValue(
  el: HTMLInputElement | HTMLTextAreaElement,
  value: string
) {
  const proto =
    el.tagName.toLowerCase() === 'textarea'
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  const descriptor = Object.getOwnPropertyDescriptor(proto, 'value');
  const clean = sanitizeEmrText(value);
  descriptor?.set
    ? descriptor.set.call(el, clean)
    : ((el as any).value = clean);
}

function setReactInput(
  el: HTMLInputElement | HTMLTextAreaElement,
  next: string
) {
  if ((el.value || '') === next) return; // ✅ guard

  // Check if element is already being updated to prevent infinite loops
  if ((el as any).__updating) return;

  // Use queue system for all sections to prevent concurrent updates and React error #185
  updateQueue.push(() => {
    if ((el as any).__updating) return;
    (el as any).__updating = true;
    setNativeValue(el, next);
    // Dispatch input event (skip change to reduce React updates)
    setTimeout(() => {
      try {
        el.dispatchEvent(
          new Event('input', { bubbles: true, cancelable: true })
        );
      } finally {
        delete (el as any).__updating;
      }
    }, 100);
  });
  processUpdateQueue();
}

function handleTextareaInput(textarea, value, replyType = 'overwrite') {
  if (!textarea || isEmptyOrNull(value)) return;
  const next =
    replyType === 'append'
      ? `${textarea.value || ''}\n${String(value)}`.trim()
      : String(value);
  setReactInput(textarea, next);
}

function handleTextInput(input, value, replyType = 'overwrite') {
  if (!input || isEmptyOrNull(value)) return;
  const next =
    replyType === 'append'
      ? `${input.value || ''} ${String(value)}`.trim()
      : String(value);
  setReactInput(input, next);
}

function handleCheckboxInput(checkbox, checked = true) {
  if (!checkbox) return;
  if (checkbox.checked === checked) return; // Already in desired state

  // Check if checkbox is already being updated to prevent infinite loops
  if ((checkbox as any).__updating) return;

  // Use queue system for all sections to prevent React error #185
  updateQueue.push(() => {
    if ((checkbox as any).__updating) return;
    (checkbox as any).__updating = true;
    setTimeout(() => {
      try {
        checkbox.click();
      } finally {
        delete (checkbox as any).__updating;
      }
    }, 100);
  });
  processUpdateQueue();
}

function handleDropdownInput(
  input,
  value,
  choices: string[] = [],
  useQuickMode = false
) {
  if (!input || !value) return;

  const autocompleteRoot = input.closest('.MuiAutocomplete-root');
  if (!autocompleteRoot) {
    // Not an autocomplete, just set the value
    (input as HTMLInputElement).value = sanitizeEmrText(value);
    // Use requestAnimationFrame to avoid React error #185
    requestAnimationFrame(() => {
      input.dispatchEvent(
        new Event('input', { bubbles: true, cancelable: true })
      );
      requestAnimationFrame(() => {
        input.dispatchEvent(
          new Event('change', { bubbles: true, cancelable: true })
        );
      });
    });
    return;
  }

  const goalTypeNormalizations: Record<string, string> = {
    stg: 'Short Term',
    'short term goal': 'Short Term',
    'short-term': 'Short Term',
    ltg: 'Long Term',
    'long term goal': 'Long Term',
    'long-term': 'Long Term'
  };

  const valueStr = sanitizeEmrText(value).trim();
  const valueLower = valueStr.toLowerCase();
  const normalizedValueStr = goalTypeNormalizations[valueLower] || valueStr;
  const normalizedValueLower = normalizedValueStr.toLowerCase();

  // Function to find and select the matching option
  const selectOption = () => {
    // Look for options in the document (they might be in a portal)
    const allOptions = document.querySelectorAll('[role="option"]');
    let matchingOption: HTMLElement | null = null;

    // First try exact match against the value or its normalized form (case-insensitive)
    for (const opt of Array.from(allOptions)) {
      const optText = (opt as HTMLElement).textContent?.trim();
      const optLower = optText?.toLowerCase();
      if (optLower === valueLower || optLower === normalizedValueLower) {
        matchingOption = opt as HTMLElement;
        break;
      }
    }

    // If no exact match, try partial match
    if (!matchingOption) {
      for (const opt of Array.from(allOptions)) {
        const optText = (opt as HTMLElement).textContent?.trim().toLowerCase();
        if (
          optText &&
          (optText === valueLower ||
            optText === normalizedValueLower ||
            optText.includes(normalizedValueLower) ||
            normalizedValueLower.includes(optText) ||
            optText.includes(valueLower) ||
            valueLower.includes(optText))
        ) {
          matchingOption = opt as HTMLElement;
          break;
        }
      }
    }

    if (matchingOption) {
      // Scroll the option into view first
      matchingOption.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      // Use mouse events for better compatibility with MUI
      matchingOption.dispatchEvent(
        new MouseEvent('mousedown', { bubbles: true, cancelable: true })
      );
      matchingOption.dispatchEvent(
        new MouseEvent('mouseup', { bubbles: true, cancelable: true })
      );
      // Click the option to select it
      matchingOption.click();
      return true;
    }
    return false;
  };

  // Quick mode: synchronous flow for objective table to prevent hanging
  // Avoids long setTimeout chains that can cause UI to hang when filling many fields
  if (useQuickMode) {
    // Set value and open dropdown
    (input as HTMLInputElement).value = valueStr;
    // Use requestAnimationFrame to avoid React error #185
    requestAnimationFrame(() => {
      input.dispatchEvent(
        new Event('input', { bubbles: true, cancelable: true })
      );
    });

    const popupButton = autocompleteRoot.querySelector(
      'button[aria-label="Open"], button[title="Open"], .MuiAutocomplete-popupIndicator'
    ) as HTMLElement;
    if (popupButton) {
      const isOpen =
        autocompleteRoot.getAttribute('aria-expanded') === 'true' ||
        popupButton.getAttribute('aria-label') === 'Close' ||
        popupButton.getAttribute('title') === 'Close';

      if (!isOpen) {
        popupButton.click();
      }
    }

    // Try to select immediately - options might already be in DOM or render quickly
    if (selectOption()) {
      requestAnimationFrame(() => {
        input.dispatchEvent(
          new Event('change', { bubbles: true, cancelable: true })
        );
      });
    } else {
      // Fallback: if value already matches (e.g., "Short Term" shown), just ensure change is fired
      // Otherwise set value directly - don't use long retry loops that cause hanging
      (input as HTMLInputElement).value = valueStr;
      requestAnimationFrame(() => {
        input.dispatchEvent(
          new Event('input', { bubbles: true, cancelable: true })
        );
        requestAnimationFrame(() => {
          input.dispatchEvent(
            new Event('change', { bubbles: true, cancelable: true })
          );
        });
      });
    }
    return;
  }

  // Standard mode: full async flow for Goals and other sections
  // Focus the input first
  (input as HTMLInputElement).focus();

  // Clear any existing value first (click clear button if present)
  const clearButton = autocompleteRoot.querySelector(
    'button[aria-label="Clear"], button[title="Clear"]'
  ) as HTMLElement;
  if (clearButton && (input as HTMLInputElement).value) {
    clearButton.click();
    // Wait a bit for the clear to complete
    setTimeout(() => {
      proceedWithSelection();
    }, 50);
  } else {
    proceedWithSelection();
  }

  function proceedWithSelection() {
    // Set the value in the input
    (input as HTMLInputElement).value = valueStr;
    // Use requestAnimationFrame to avoid React error #185
    requestAnimationFrame(() => {
      input.dispatchEvent(
        new Event('input', { bubbles: true, cancelable: true })
      );
    });

    // Try to open the dropdown
    const popupButton = autocompleteRoot.querySelector(
      'button[aria-label="Open"], button[title="Open"], .MuiAutocomplete-popupIndicator'
    ) as HTMLElement;
    if (popupButton) {
      // Check if dropdown is already open
      const isOpen =
        autocompleteRoot.getAttribute('aria-expanded') === 'true' ||
        popupButton.getAttribute('aria-label') === 'Close' ||
        popupButton.getAttribute('title') === 'Close';

      if (!isOpen) {
        // Open the dropdown
        popupButton.click();
      }

      // Wait for options to appear and then select
      const trySelect = (attempts = 0) => {
        if (attempts > 20) {
          // Fallback: just set the value and trigger change
          (input as HTMLInputElement).value = valueStr;
          requestAnimationFrame(() => {
            input.dispatchEvent(
              new Event('input', { bubbles: true, cancelable: true })
            );
            requestAnimationFrame(() => {
              input.dispatchEvent(
                new Event('change', { bubbles: true, cancelable: true })
              );
              // Try to blur to trigger autocomplete selection
              (input as HTMLInputElement).blur();
            });
          });
          return;
        }

        if (selectOption()) {
          // Successfully selected
          requestAnimationFrame(() => {
            input.dispatchEvent(
              new Event('change', { bubbles: true, cancelable: true })
            );
          });
        } else {
          // Options not ready yet, try again
          setTimeout(() => trySelect(attempts + 1), 50);
        }
      };

      setTimeout(() => trySelect(), 150);
    } else {
      // No popup button, try to select from existing options or just set value
      if (selectOption()) {
        requestAnimationFrame(() => {
          input.dispatchEvent(
            new Event('change', { bubbles: true, cancelable: true })
          );
        });
        return;
      }
      // Fallback: set value directly and trigger change
      (input as HTMLInputElement).value = valueStr;
      requestAnimationFrame(() => {
        input.dispatchEvent(
          new Event('input', { bubbles: true, cancelable: true })
        );
        requestAnimationFrame(() => {
          input.dispatchEvent(
            new Event('change', { bubbles: true, cancelable: true })
          );
        });
      });
    }
  }
}

function handleDatePickerInput(input: HTMLInputElement, value: string) {
  if (!input || isEmptyOrNull(value)) return;

  const dateStr = value.toString().trim();

  updateQueue.push(() => {
    if ((input as any).__updating) return;
    (input as any).__updating = true;

    setTimeout(() => {
      // Strategy 1: direct native value set (works for simple React/masked inputs)
      setNativeValue(input, dateStr);
      input.dispatchEvent(
        new Event('input', { bubbles: true, cancelable: true })
      );

      setTimeout(() => {
        const digitsExpected = dateStr.replace(/\D/g, '');
        const digitsActual = input.value.replace(/\D/g, '');

        if (digitsActual === digitsExpected) {
          input.dispatchEvent(
            new Event('change', { bubbles: true, cancelable: true })
          );
          input.blur();
          delete (input as any).__updating;
          return;
        }

        // Strategy 2: keyboard simulation one character at a time with delays
        input.focus();
        input.dispatchEvent(
          new KeyboardEvent('keydown', {
            key: 'a',
            code: 'KeyA',
            keyCode: 65,
            ctrlKey: true,
            bubbles: true,
            cancelable: true
          })
        );
        input.select();

        const digits = digitsExpected;
        let i = 0;

        const typeNextChar = () => {
          if (i >= digits.length) {
            input.dispatchEvent(
              new Event('change', { bubbles: true, cancelable: true })
            );
            input.blur();
            delete (input as any).__updating;
            return;
          }

          const char = digits[i++];
          const keyCode = char.charCodeAt(0);

          input.dispatchEvent(
            new KeyboardEvent('keydown', {
              key: char,
              code: `Digit${char}`,
              keyCode,
              which: keyCode,
              bubbles: true,
              cancelable: true
            })
          );
          input.dispatchEvent(
            new KeyboardEvent('keypress', {
              key: char,
              code: `Digit${char}`,
              keyCode,
              which: keyCode,
              charCode: keyCode,
              bubbles: true,
              cancelable: true
            })
          );
          input.dispatchEvent(
            new KeyboardEvent('keyup', {
              key: char,
              code: `Digit${char}`,
              keyCode,
              which: keyCode,
              bubbles: true,
              cancelable: true
            })
          );

          setTimeout(typeNextChar, 50);
        };

        setTimeout(typeNextChar, 50);
      }, 100);
    }, 100);
  });
  processUpdateQueue();
}

function handleSingleChoiceToggle(groupElement, optionLabel) {
  if (!groupElement || !optionLabel) return;
  const buttons = groupElement.querySelectorAll('button');
  const targetButton = Array.from(buttons).find((btn) => {
    const btnEl = btn as HTMLButtonElement;
    const isSelected = btnEl.getAttribute('aria-pressed') === 'true';
    const label = btnEl.textContent?.trim().toLowerCase();
    return label === optionLabel.trim().toLowerCase() && !isSelected;
  });

  if (targetButton) {
    // Use queue system for all sections to prevent React error #185
    updateQueue.push(() => {
      const btn = targetButton as HTMLButtonElement;
      if ((btn as any).__updating) return;
      (btn as any).__updating = true;
      setTimeout(() => {
        try {
          btn.click();
        } finally {
          delete (btn as any).__updating;
        }
      }, 100);
    });
    processUpdateQueue();
  }
}

function isEmptyOrNull(value) {
  return (
    value === null || value === undefined || value === '' || value === 'None'
  );
}

export function answerQuestions(answers) {
  /*
          Description: This function will take the answers and populate the questions
          
          Args: 
              answers: A dictionary of answers
                  "Question": "The question in question"
                  "Answer": "The answer to the question"
         
          Return: A list of any questions that were not answered, perhaps the answer didn't match the questions expected or whatnot...
              [
                  {
                      Question: "Where was the injury?", 
                      Answer: "Back" 
                      Error: "The error message" (Choice not found, etc)
                  },
                  ...
              ]
      
          */
  var errors = [];
  answers.forEach((answer) => {
    var error = answerQuestion(
      answer['Question'],
      answer['Answer'],
      answer['Reply Type']
    );
    if (error) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  });
  console.error('Errors:');
  console.error(errors);
  return errors;
}

export async function answerQuestion(question, answer, replyType) {
  /*
          Description: This function will take the answer and populate the question
          
          Args: 
              question: The question in question
              answer: The answer to the question
         
          Return: null if success otherwise a string with the error message
          */

  // console.log("Answering Question: '" + question + "' with '" + answer+"'")

  // Make sure the question exists
  if (questionInteractions[question] == null) {
    console.error('Question not found');
    console.error(question);
    // console.log(questionInteractions)
    // console.log(Object.keys(questionInteractions))

    return 'Question not found';
  }

  // If it's a flex do so
  if (questionInteractions[question]['Type'] === 'Flex') {
    return await answerFlexQuestion(question, answer, replyType);
  }

  // If it is a Long Answer or long answer, just populate the input
  if (questionInteractions[question]['Input']) {
    questionInteractions[question]['Input'](answer, replyType);
    return true;
  }

  // Check if the answer is a list of answers or just a single answer
  if (Array.isArray(answer)) {
    // Choose All
    var notFoundAnswers = [];
    for (var i = 0; i < answer.length; i++) {
      if (answer[i] in questionInteractions[question]['Choices']) {
        console.log('Answer found ' + answer[i]);
        questionInteractions[question]['Choices'][answer[i]](true);
        // sleep(1000).then(() => {
        //     questionInteractions[question][answer[i]]()
        // });
      } else {
        notFoundAnswers.push(answer[i]);
      }
    }
    if (notFoundAnswers.length > 0) {
      console.error('The following answers were not found: ' + notFoundAnswers);
      return 'The following answers were not found: ' + notFoundAnswers;
    } else {
      return true;
    }
  } else {
    // Multiple Choice
    if (answer in questionInteractions[question]['Choices']) {
      questionInteractions[question]['Choices'][answer]();
      return true;
    } else {
      console.error('Answer not found');
      console.log('Question: ' + question);
      console.log('Answer:' + answer);
      console.log(questionInteractions[question]['Choices']);
      return 'Answer not found';
    }
  }
}

async function answerFlexQuestion(question, answer, replyType = 'overwrite') {
  try {
    const errors = [];
    const questionObject = questionInteractions[question];

    if (!questionObject) {
      console.error(`Question with Key "${question}" not found.`);
      return {
        Question: question,
        Error: `Question with Key "${question}" not found.`
      };
    }

    console.log('Question found:', questionObject);

    const structure = questionObject.Structure || {};

    /** Do the answering sequentially */
    for (const [fieldKey, fieldDetails] of Object.entries(answer)) {
      const field = structure[fieldKey];

      if (!field) {
        console.error(`Field "${fieldKey}" not found in the Structure.`);
        errors.push({ Field: fieldKey, Error: 'Field not found in Structure' });
        continue;
      }

      for (const [subFieldKey, value] of Object.entries(
        fieldDetails as Record<string, unknown>
      )) {
        // Handle comma-separated values for checkbox inputs
        const valuesToInput =
          typeof value === 'string'
            ? value.split('$').map((v) => v.trim())
            : [value];

        for (const val of valuesToInput) {
          if (field[subFieldKey]?.Input) {
            try {
              await field[subFieldKey].Input(val, replyType);
              // Longer delay for Objective table fields so React can re-render before next resolver runs
              const delayMs = question === 'Objective' ? 150 : 100;
              await new Promise((resolve) => setTimeout(resolve, delayMs));
            } catch (error) {
              errors.push({
                Field: `${fieldKey}.${subFieldKey}`,
                Error: `Error applying value: ${error.message}`
              });
            }
          } else {
            console.warn(
              `Input function not found for "${fieldKey}.${subFieldKey}"`
            );
            errors.push({
              Field: `${fieldKey}.${subFieldKey}`,
              Error: 'Input function not found'
            });
          }
        }
      }
    }

    /** Parse the errors */
    if (errors.length > 0) {
      console.error('Errors occurred:', errors);
      return errors;
    }

    return null; // Success
  } catch (error) {
    console.error('Unexpected error:', error);
    return {
      Question: question,
      Error: `Unexpected error: ${error.message}`
    };
  }
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    // Return the full HTML content of the document
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector();
  }
}

/*
Get All Notes for Stride is event-driven and section-level (NoteFocusCapture.ts)
rather than the polling in NoteCapture.ts. It has its own export on purpose:
the standard *ForCapture names would also start that polling and switch on the
PUSH_ANSWER note-key check and ComplianceRail's capture reader for Stride.

A section is one child of the note root, headed by its name ("Subjective",
"Objective", ...). It is read by the same extractors getQuestions uses, limited
to that section's element, so its keys are exactly getQuestions' keys.
*/
const NOTE_ROOT_ID = 'template-section-container';

export const noteFocusCapture: FocusCaptureSource = {
  getNoteKey: getNoteKeyForFocusCapture,
  // Stride shows no note id on the page; a new note renders a new root.
  getNoteStamp: () => document.getElementById(NOTE_ROOT_ID),
  sectionOf: (node) => {
    const element = node instanceof Element ? node : node.parentElement;
    const section = element?.closest(`#${NOTE_ROOT_ID} > div`);
    return section ? sectionName(section) : null;
  },
  listSections: () => noteSections().map(([name]) => name),
  readSection: readSectionForFocusCapture,
  getPatientInfo,
  getPageInfo,
  isFinalization: isNoteFinalizationSubmit,
  observedAttributes: [
    'aria-checked',
    'aria-pressed',
    'aria-valuenow',
    'checked',
    'value'
  ]
};

/*
The Note Finalization dialog is a form titled #lock-note-modal. Both "Sign the
note" and "Request Signature" submit it, and the browser holds the submit back
while a required field (Rendering Clinician) is empty, so a submit is the
moment the clinician finished the note.
*/
function isNoteFinalizationSubmit(event: Event) {
  return (
    event.type === 'submit' &&
    event.target instanceof HTMLFormElement &&
    Boolean(event.target.querySelector('#lock-note-modal'))
  );
}

// Stride opens every note at /notes/<id>; ids are per practice database, so
// the host keeps two practices' note 125 apart.
function getNoteKeyForFocusCapture(): string | null {
  const noteId = window.location.pathname.match(/\/notes\/(\d+)/)?.[1];
  return noteId ? `${window.location.hostname}|note:${noteId}` : null;
}

function sectionName(section: Element): string | null {
  const header = section.querySelector(':scope > [class*="header"]');
  return sanitizeKey(header?.textContent?.trim() || '') || null;
}

function noteSections(): Array<[string, Element]> {
  const root = document.getElementById(NOTE_ROOT_ID);
  if (!root) return [];
  return Array.from(root.children).flatMap((section) => {
    const name = sectionName(section);
    return name ? [[name, section] as [string, Element]] : [];
  });
}

/**
 * One section's current values, keyed like getQuestions: { [section]:
 * { [subsection]: { [field]: value } } }. Runs the extractors into a private map, so a capture
 * between GET_DOM and PUSH_ANSWER never replaces the questionInteractions that
 * answerQuestion fills from. It only reads the DOM.
 */
function readSectionForFocusCapture(section: string): SectionRead | null {
  const element = noteSections().find(([name]) => name === section)?.[1];
  if (!element) return null;

  const savedInteractions = questionInteractions;
  const savedNoteType = cachedNoteType;
  questionInteractions = {};
  cachedNoteType = null;
  capturingValues = true;
  captureScope = element;
  sectionNamesForCapture = new Map();
  try {
    extractTextInputsAndTextareas();
    extractCheckboxes();
    extractSingleChoiceToggles();
    extractObjectiveTable();
    extractFlowsheet();
    normalizeObjectiveStructureKeys();
    pruneGoalsForNonInitialEval();
    // An empty section is still saved, so fields the clinician removed go.
    return {
      values: {
        [section]: readCapturedFieldValues(questionInteractions[section]?.Structure)
      }
    };
  } finally {
    questionInteractions = savedInteractions;
    cachedNoteType = savedNoteType;
    capturingValues = false;
    captureScope = null;
    sectionNamesForCapture = null;
  }
}

function readCapturedFieldValues(fields: Record<string, any>) {
  const values: Record<string, any> = {};
  Object.entries(fields || {}).forEach(([key, field]) => {
    if (!field || typeof field !== 'object') return;
    if ('Existing Value' in field) {
      values[key] = field['Existing Value'];
      return;
    }
    const nested = readCapturedFieldValues(field);
    if (Object.keys(nested).length > 0) values[key] = nested;
  });
  return values;
}

function getPressedToggleLabel(group: Element): string {
  const pressed = Array.from(group.querySelectorAll('button')).find(
    (button) => button.getAttribute('aria-pressed') === 'true'
  );
  return pressed?.textContent?.trim().replace(/\s+/g, ' ') || '';
}
